// =============================================================================
// BP-07-03: ResourceContent Component (Rich Text Renderer)
// =============================================================================

import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { ExternalLink, Copy, Check } from 'lucide-react';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface ResourceContentProps {
  content: string;
  format?: 'markdown' | 'html';
  className?: string;
  onHeadingsChange?: (headings: TableOfContentsItem[]) => void;
  showProgress?: boolean;
  onProgressChange?: (percentage: number) => void;
}

export interface TableOfContentsItem {
  id: string;
  text: string;
  level: number;
}

// -----------------------------------------------------------------------------
// Code Block Component
// -----------------------------------------------------------------------------

interface CodeBlockProps {
  inline?: boolean;
  className?: string;
  children?: React.ReactNode;
}

function CodeBlock({ inline, className, children }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);
  const codeRef = useRef<HTMLElement>(null);
  
  const language = className?.replace('language-', '') || '';
  const codeContent = String(children).replace(/\n$/, '');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codeContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (inline) {
    return (
      <code className="px-1.5 py-0.5 text-sm bg-cream-dark text-terracotta rounded font-mono">
        {children}
      </code>
    );
  }

  return (
    <div className="relative group my-4">
      <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={handleCopy}
          className="p-1.5 rounded bg-white/10 hover:bg-white/20 transition-colors"
          aria-label="Copy code"
        >
          {copied ? (
            <Check className="w-4 h-4 text-green-400" />
          ) : (
            <Copy className="w-4 h-4 text-white/60" />
          )}
        </button>
      </div>
      {language && (
        <div className="absolute left-3 top-2 text-xs text-white/40 font-mono">
          {language}
        </div>
      )}
      <pre className="p-4 pt-8 rounded-lg bg-navy overflow-x-auto">
        <code ref={codeRef} className={cn('text-sm font-mono text-white/90', className)}>
          {children}
        </code>
      </pre>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Custom Link Component
// -----------------------------------------------------------------------------

interface CustomLinkProps {
  href?: string;
  children?: React.ReactNode;
}

function CustomLink({ href, children }: CustomLinkProps) {
  const isExternal = href?.startsWith('http');
  
  return (
    <a
      href={href}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      className="text-sage hover:text-sage-dark underline underline-offset-2 decoration-sage/30 hover:decoration-sage inline-flex items-center gap-0.5"
    >
      {children}
      {isExternal && <ExternalLink className="w-3 h-3 ml-0.5" />}
    </a>
  );
}

// -----------------------------------------------------------------------------
// Reading Progress Hook
// -----------------------------------------------------------------------------

function useReadingProgressInternal(containerRef: React.RefObject<HTMLElement>,
  onProgressChange?: (percentage: number) => void
) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { top, height } = container.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      // Calculate how much of the content has been scrolled past
      const scrolled = Math.max(0, -top);
      const total = height - viewportHeight;
      const percentage = Math.min(100, Math.max(0, (scrolled / total) * 100));
      
      setProgress(Math.round(percentage));
      onProgressChange?.(Math.round(percentage));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial calculation

    return () => window.removeEventListener('scroll', handleScroll);
  }, [containerRef, onProgressChange]);

  return progress;
}

// -----------------------------------------------------------------------------
// Extract Headings
// -----------------------------------------------------------------------------

function extractHeadings(content: string): TableOfContentsItem[] {
  const headingRegex = /^(#{1,6})\s+(.+)$/gm;
  const headings: TableOfContentsItem[] = [];
  let match;

  while ((match = headingRegex.exec(content)) !== null) {
    const level = match[1].length;
    const text = match[2].trim();
    const id = text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    
    headings.push({ id, text, level });
  }

  return headings;
}

// -----------------------------------------------------------------------------
// ResourceContent Component
// -----------------------------------------------------------------------------

export function ResourceContent({
  content,
  format = 'markdown',
  className,
  onHeadingsChange,
  showProgress = false,
  onProgressChange,
}: ResourceContentProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const progress = useReadingProgressInternal(containerRef, onProgressChange);

  // Extract and report headings
  useEffect(() => {
    if (onHeadingsChange && format === 'markdown') {
      const headings = extractHeadings(content);
      onHeadingsChange(headings);
    }
  }, [content, format, onHeadingsChange]);

  // Generate heading with ID
  const generateHeading = (level: 1 | 2 | 3 | 4 | 5 | 6) => {
    const Tag = `h${level}` as const;
    const sizes = {
      1: 'text-3xl font-display font-semibold mt-8 mb-4',
      2: 'text-2xl font-display font-semibold mt-8 mb-3',
      3: 'text-xl font-display font-medium mt-6 mb-2',
      4: 'text-lg font-medium mt-4 mb-2',
      5: 'text-base font-medium mt-4 mb-2',
      6: 'text-sm font-medium mt-4 mb-2 text-warm-gray',
    };

    return ({ children }: { children?: React.ReactNode }) => {
      const text = String(children);
      const id = text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      return (
        <Tag id={id} className={cn(sizes[level], 'text-navy scroll-mt-24')}>
          <a href={`#${id}`} className="hover:text-sage-dark transition-colors">
            {children}
          </a>
        </Tag>
      );
    };
  };

  // Custom components for ReactMarkdown
  const components = {
    h1: generateHeading(1),
    h2: generateHeading(2),
    h3: generateHeading(3),
    h4: generateHeading(4),
    h5: generateHeading(5),
    h6: generateHeading(6),
    p: ({ children }: { children?: React.ReactNode }) => (
      <p className="my-4 leading-relaxed text-navy">{children}</p>
    ),
    a: CustomLink,
    code: CodeBlock,
    ul: ({ children }: { children?: React.ReactNode }) => (
      <ul className="my-4 ml-6 list-disc space-y-2">{children}</ul>
    ),
    ol: ({ children }: { children?: React.ReactNode }) => (
      <ol className="my-4 ml-6 list-decimal space-y-2">{children}</ol>
    ),
    li: ({ children }: { children?: React.ReactNode }) => (
      <li className="text-navy">{children}</li>
    ),
    blockquote: ({ children }: { children?: React.ReactNode }) => (
      <blockquote className="my-6 pl-4 border-l-4 border-sage/30 italic text-warm-gray">
        {children}
      </blockquote>
    ),
    img: ({ src, alt }: { src?: string; alt?: string }) => (
      <figure className="my-6">
        <img
          src={src}
          alt={alt || ''}
          className="rounded-lg w-full"
          loading="lazy"
        />
        {alt && (
          <figcaption className="mt-2 text-center text-sm text-warm-gray">
            {alt}
          </figcaption>
        )}
      </figure>
    ),
    table: ({ children }: { children?: React.ReactNode }) => (
      <div className="my-6 overflow-x-auto">
        <table className="w-full border-collapse">{children}</table>
      </div>
    ),
    thead: ({ children }: { children?: React.ReactNode }) => (
      <thead className="bg-cream-dark">{children}</thead>
    ),
    th: ({ children }: { children?: React.ReactNode }) => (
      <th className="px-4 py-2 text-left text-sm font-medium text-navy border border-warm-gray/20">
        {children}
      </th>
    ),
    td: ({ children }: { children?: React.ReactNode }) => (
      <td className="px-4 py-2 text-sm border border-warm-gray/20">{children}</td>
    ),
    hr: () => <hr className="my-8 border-warm-gray/20" />,
  };

  return (
    <>
      {/* Progress Bar */}
      {showProgress && (
        <div className="fixed top-0 left-0 right-0 h-1 bg-cream-dark z-50">
          <div
            className="h-full bg-sage transition-all duration-100"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {/* Content */}
      <div ref={containerRef} className={cn('resource-content', className)}>
        {format === 'markdown' ? (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw, rehypeSanitize]}
            components={components}
          >
            {content}
          </ReactMarkdown>
        ) : (
          <div
            className="prose prose-navy max-w-none"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        )}
      </div>
    </>
  );
}

// -----------------------------------------------------------------------------
// Table of Contents Component
// -----------------------------------------------------------------------------

interface TableOfContentsProps {
  headings: TableOfContentsItem[];
  activeId?: string;
  className?: string;
}

export function TableOfContents({ headings, activeId, className }: TableOfContentsProps) {
  if (headings.length === 0) return null;

  return (
    <nav className={cn('space-y-1', className)}>
      <h4 className="font-medium text-navy mb-3">On This Page</h4>
      {headings.map((heading) => (
        <a
          key={heading.id}
          href={`#${heading.id}`}
          className={cn(
            'block py-1 text-sm transition-colors',
            heading.level > 2 && 'ml-3',
            heading.level > 3 && 'ml-6',
            activeId === heading.id
              ? 'text-sage font-medium'
              : 'text-warm-gray hover:text-navy'
          )}
        >
          {heading.text}
        </a>
      ))}
    </nav>
  );
}


export function useReadingProgress() {
  const [progress, setProgress] = React.useState(0);
  
  React.useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      setProgress(Math.min(100, Math.max(0, scrollPercent)));
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  return progress;
}

export default ResourceContent;

// Extract table of contents from markdown/HTML content
export function extractTableOfContents(content: string): { id: string; title: string; level: number }[] {
  const headingRegex = /<h([1-6])[^>]*id="([^"]*)"[^>]*>([^<]*)<\/h[1-6]>/gi;
  const toc: { id: string; title: string; level: number }[] = [];
  let match;
  
  while ((match = headingRegex.exec(content)) !== null) {
    toc.push({
      level: parseInt(match[1], 10),
      id: match[2],
      title: match[3],
    });
  }
  
  return toc;
}


